import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  buildWpsFieldNavTarget,
  type WpsFieldDescriptor,
  type WpsFieldLinkContext
} from './wpsFieldLinkRegistry'

/**
 * Shared hover / focus affordance applied to every enabled WPS
 * preview field-link wrap (slice 07 of `wps-preview-pixel-parity`).
 *
 *  - `hover:bg-[color:var(--wps-hover-bg)]` paints the ~4% black
 *    overlay token defined in `wpsPreviewTheme.cssVars`.
 *  - `focus-visible:outline-none` + matching brand-red ring mirrors
 *    the rest of the app's button affordance so keyboard users see
 *    a clear focus indicator.
 *  - `transition-colors` keeps the hover state from snapping
 *    abruptly when scanning across cells.
 *
 * Exported so the slice 07 affordance test can assert the same
 * class list lives on a rendered wrap without duplicating the
 * literals between the production code and the test.
 */
export const WPS_FIELD_LINK_AFFORDANCE_CLASS = [
  'transition-colors',
  'hover:bg-[color:var(--wps-hover-bg)]',
  'focus-visible:outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-[color:var(--wps-brand-red)]',
  'focus-visible:ring-offset-1'
].join(' ')

/**
 * Canvas-side wrapper that turns a region of the WPS Preview into an
 * edit affordance — either navigates to the source form page (URL
 * targets) or opens an in-place modal on the preview page (modal
 * targets). Slice 08 of `wps-pdf-xlsx-faithful` established the
 * navigate-only behaviour; slice 04 of `wps-preview-pixel-parity`
 * adds the modal-target branch so the TopBand identifiers pill and
 * the ApprovalsFooter signature blocks can re-use the existing
 * modals on `WpsPreviewPage` without leaving the preview context.
 *
 * Click resolution:
 *   - `target === null`                  → inert (`aria-disabled`)
 *   - `target.kind === 'modal'`          → `context.onModalOpen(...)`
 *                                          (or inert when the page
 *                                          didn't supply that hook)
 *   - `target.kind === 'nav'`            → `navigate(target.fullUrl)`
 */

interface Props {
  descriptor: WpsFieldDescriptor
  /** Human label for the clicked field, e.g. "Material Spec". */
  fieldLabel: string
  context: WpsFieldLinkContext
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  /**
   * Outer element to render. Defaults to `'div'`. Use `'td'` when
   * the link wraps a single table cell so the affordance stays
   * inside a `<tr>` without HTML validation errors. Slice 05 of
   * `wps-preview-pixel-parity` uses this for the per-pass cells in
   * the WPS Preview's Welding Parameters table.
   */
  as?: 'div' | 'td'
  /** Forwarded to the outer element when `as === 'td'`. */
  colSpan?: number
}

export default function WpsPreviewFieldLink({
  descriptor,
  fieldLabel,
  context,
  children,
  className,
  style,
  as = 'div',
  colSpan
}: Props) {
  const navigate = useNavigate()
  const target = buildWpsFieldNavTarget(descriptor, context)
  // Modal targets fall back to inert when the consuming page did not
  // supply an `onModalOpen` hook — this keeps read-only embeds + the
  // existing test fixtures (which don't wire modals) from blowing up.
  const isModalWithoutHandler =
    target !== null && target.kind === 'modal' && !context.onModalOpen
  const disabled = target === null || isModalWithoutHandler

  const dispatch = () => {
    if (!target) return
    if (target.kind === 'modal') {
      if (!context.onModalOpen) return
      context.onModalOpen(target.modalKey)
      return
    }
    // Slice C.4 of `wps-product-parity-fixes` — when the page
    // supplies a `requestNavigate` guard, gate every URL-target
    // click through it so `WpsNavigateConfirmModal` can interpose
    // (clean = Continue, dirty = Save & Continue / Discard &
    // Continue). When the guard is absent (read-only embeds, the
    // existing test fixtures) we fall back to the historical
    // immediate-navigate behaviour.
    if (context.requestNavigate) {
      context.requestNavigate({
        target,
        fieldLabel,
        proceed: () => navigate(target.fullUrl)
      })
      return
    }
    navigate(target.fullUrl)
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      dispatch()
    }
  }

  // Slice 07 of `wps-preview-pixel-parity` — uniform hover/focus
  // affordance. Every enabled wrap shows `cursor: pointer` + a
  // subtle hover overlay using the shared `--wps-hover-bg` token (a
  // ~4% black). Disabled wraps swap to `cursor: not-allowed` and
  // skip the overlay. Class-level styles live in
  // `WPS_FIELD_LINK_AFFORDANCE_CLASS` so jsdom tests can assert the
  // hover / focus-visible utility presence on the resolved
  // `className` without relying on CSSOM `var(--…)` resolution.
  const sharedProps = {
    role: 'button',
    tabIndex: disabled ? -1 : 0,
    'aria-label': fieldLabel,
    'aria-disabled': disabled ? ('true' as const) : undefined,
    title: target ? `Edit in ${target.destinationLabel}` : undefined,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    className: [className, !disabled ? WPS_FIELD_LINK_AFFORDANCE_CLASS : undefined]
      .filter(Boolean)
      .join(' '),
    style: {
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      ...style
    }
  }

  if (as === 'td') {
    return (
      <td {...sharedProps} colSpan={colSpan}>
        {children}
      </td>
    )
  }
  return <div {...sharedProps}>{children}</div>
}
